import { useState, useRef, useEffect, useCallback } from "react";
import moment from "moment";
import "./TimePicker.scss";

export default function TimePicker({
  id,
  labelText,
  timepickerValue: value,
  changeHandler,
  errorMessage = "Invalid time format",
  invalidFormatErrorMessage = "Invalid time format",
  inputState = "default", // "default" | "error" | "disabled"
  isAutoErrorValidation = true, // if false, no format validation/error from component; use inputState + validate externally
  format = "12", // "12" or "24" hour format
  minuteStep = 5, // Step for minutes (5, 10, 15, 30, etc.)
  type = "advanced", // "simple" | "advanced"
}) {
  const Icon = window.sharedWebcomponents.Icon;
  const [open, setOpen] = useState(false);
  const [formatError, setFormatError] = useState(null); // Invalid time format error (keep value, show message)
  const containerRef = useRef();
  const hoursRef = useRef();
  const minutesRef = useRef();
  const ampmRef = useRef();
  const simpleDropdownRef = useRef();

  // Parse current value - supports multiple formats for manual input
  const parseTime = (timeString) => {
    if (!timeString || typeof timeString !== "string") return null;
    
    const trimmed = timeString.trim();
    if (!trimmed) return null;
    
    try {
      // Try multiple formats for flexible input.
      // 12-hour: only h/hh (1-12), not H (0-23), so e.g. "23:34 AM" is invalid
      const formats = format === "12"
        ? ["h:mm A", "h:mmA", "h:mm", "h:m A", "h:mA", "h:m", "hh:mm A", "hh:mmA", "hh:mm"]
        : ["HH:mm", "H:mm", "HH:m", "H:m", "h:mm A", "h:mmA", "h:mm"];
      
      const time = moment(trimmed, formats, true); // strict parsing
      if (time.isValid()) {
        if (format === "12") {
          const hour24 = parseInt(time.format("HH"));
          const hour12 = hour24 === 0 ? 12 : (hour24 > 12 ? hour24 - 12 : hour24);
          return {
            hour: hour12,
            minute: parseInt(time.format("mm")),
            ampm: time.format("A"),
          };
        } else {
          return {
            hour: parseInt(time.format("HH")),
            minute: parseInt(time.format("mm")),
            ampm: null,
          };
        }
      }
    } catch (e) {
      // Fallback
    }
    return null;
  };

  const [selectedTime, setSelectedTime] = useState(parseTime(value));
  const [tempTime, setTempTime] = useState(null); // Temporary time during selection
  const [inputValue, setInputValue] = useState(value || ""); // For manual input
  const [isEditing, setIsEditing] = useState(false); // Track if user is manually editing

  // Update selectedTime and inputValue when value prop changes (don't overwrite while user is typing)
  useEffect(() => {
    if (isEditing) return;
    const parsed = parseTime(value);
    setSelectedTime(parsed);
    setInputValue(value || "");
    if (parsed) setFormatError(null);
  }, [value]);

  // Initialize tempTime when popup opens (only for advanced type)
  useEffect(() => {
    if (open && type === "advanced") {
      setTempTime(selectedTime);
    }
  }, [open, type, selectedTime]);

  // Generate hour options
  const generateHours = () => {
    if (format === "12") {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    } else {
      return Array.from({ length: 24 }, (_, i) => i);
    }
  };

  // Generate minute options
  const generateMinutes = () => {
    const minutes = [];
    for (let i = 0; i < 60; i += minuteStep) {
      minutes.push(i);
    }
    return minutes;
  };

  const hourOptions = generateHours();
  const minuteOptions = generateMinutes();

  // Generate simple time options (6:00 AM to 5:00 AM)
  const generateSimpleTimes = () => {
    const times = [];
    const startHour = 6; // Start at 6 AM
    for (let i = 0; i < 24; i++) {
      const hour = (startHour + i) % 24;
      const time = moment({ hour, minute: 0 });
      const formattedTime = format === "12" ? time.format("h:mm A") : time.format("HH:mm"); 
      times.push(formattedTime);
    }
    return times;
  };

  const simpleTimeOptions = type === "simple" ? generateSimpleTimes() : [];

  // Format time for display (12-hour: no leading zero on hour e.g. 1:30 AM; 24-hour: leading zeros e.g. 01:30)
  const formatTime = (time) => {
    if (!time) return "";
    const { hour, minute, ampm } = time;
    const m = minute.toString().padStart(2, "0");
    if (format === "12") {
      const h = hour.toString(); // 1-12, no leading zero
      return `${h}:${m} ${ampm || "AM"}`;
    } else {
      const h = hour.toString().padStart(2, "0");
      return `${h}:${m}`;
    }
  };

  // Handle hour selection
  const handleHourSelect = (hour) => {
    const newTime = tempTime 
      ? { ...tempTime, hour }
      : { hour, minute: 0, ampm: format === "12" ? "AM" : null };
    setTempTime(newTime);
  };

  // Handle minute selection
  const handleMinuteSelect = (minute) => {
    const newTime = tempTime
      ? { ...tempTime, minute }
      : { hour: format === "12" ? 12 : 0, minute, ampm: format === "12" ? "AM" : null };
    setTempTime(newTime);
  };

  // Handle AM/PM selection
  const handleAmPmSelect = (ampm) => {
    const newTime = tempTime
      ? { ...tempTime, ampm }
      : { hour: 12, minute: 0, ampm };
    setTempTime(newTime);
  };

  // Handle manual input change — only update local state; commit on blur or picker selection
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsEditing(true);
    // Don't call changeHandler here: partial input (e.g. "11:3") would parse and
    // trigger parent update, then useEffect would overwrite the input. Commit on blur.
  };

  // Handle input blur - validate and format
  const handleInputBlur = () => {
    setIsEditing(false);
    const parsed = parseTime(inputValue);
    if (parsed) {
      setFormatError(null);
      // Format the input value properly
      const formatted = formatTime(parsed);
      setInputValue(formatted);
      changeHandler(formatted);
      setSelectedTime(parsed);
    } else if (inputValue.trim() === "") {
      setFormatError(null);
      // Empty input is allowed
      setInputValue("");
      changeHandler("");
      setSelectedTime(null);
    } else {
      // Invalid time format: keep value and show error message (only when auto validation is on)
      if (isAutoErrorValidation) {
        setFormatError(invalidFormatErrorMessage);
      }
    }
  };

  // Handle OK button
  const handleOk = useCallback(() => {
    if (tempTime) {
      setFormatError(null);
      const timeString = formatTime(tempTime);
      changeHandler(timeString);
      setSelectedTime(tempTime);
      setInputValue(timeString);
      setIsEditing(false);
    }
    setOpen(false);
  }, [tempTime, changeHandler, format]);

  // Handle Cancel button
  const handleCancel = useCallback(() => {
    setTempTime(selectedTime); // Revert to original
    setOpen(false);
  }, [selectedTime]);

  // Handle simple time selection
  const handleSimpleTimeSelect = (timeString) => {
    setFormatError(null);
    changeHandler(timeString);
    setInputValue(timeString);
    const parsed = parseTime(timeString);
    setSelectedTime(parsed);
    setOpen(false);
  };

  // Scroll to selected item when popup opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (type === "simple") {
          // Scroll to selected item in simple dropdown
          if (simpleDropdownRef.current) {
            const selected = simpleDropdownRef.current.querySelector('.selected');
            if (selected) {
              selected.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
          }
        } else if (tempTime) {
          // Scroll to selected items in advanced picker
          const scrollToSelected = (ref) => {
            if (ref.current) {
              const selected = ref.current.querySelector('.selected');
              if (selected) {
                selected.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }
            }
          };
          scrollToSelected(hoursRef);
          scrollToSelected(minutesRef);
          if (format === "12") scrollToSelected(ampmRef);
        }
      }, 100);
    }
  }, [open, format, tempTime, type]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      if (e.key === "Escape") {
        handleCancel();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleCancel]);

  const isDisabled = inputState === "disabled";

  const hasError = inputState === "error" || (isAutoErrorValidation && formatError);
  const inputClass =
    hasError
      ? "pe-timepicker-input-styles pe-textInput-time--basic_error"
      : "pe-timepicker-input-styles";

  return (
    <div className="assignment-dates">
      <div className="pe-timepicker-main">
        {labelText && (
          <label
            htmlFor={id}
            className={
              hasError
                ? "pe-textLabelInput__label_time--label_error"
                : "pe-textLabelInput__label_time"
            }
          >
            {labelText}
          </label>
        )}

        <div
          className={`pe-timepicker-container custom-dropdown ${
            isDisabled ? "disabled" : ""
          }`}
          ref={containerRef}
        >
          <div className={inputClass}>
            <input
              id={id}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={() => {
                setIsEditing(true);
                if (!isDisabled) setOpen((prev) => !prev);
              }}
              placeholder="Select time"
              disabled={isDisabled}
              className={inputValue ? "active" : ""}
            />
            <div
              className={`time-picker-icon ${isDisabled ? "disabled" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) setOpen((prev) => !prev);
              }}
            >
              <Icon
                fileName="clock-18"
                isHidden={true}
                style={{
                  color: "rgba(0, 0, 0, 0.54)",
                }}
              />
            </div>
          </div>

          {open && (
            <div className={`timepicker-popup ${type === "simple" ? "timepicker-popup-simple" : ""}`} onClick={(e) => e.stopPropagation()}>
              {type === "simple" ? (
                <div className="timepicker-simple-dropdown" ref={simpleDropdownRef}>
                  {simpleTimeOptions.map((timeString) => (
                    <div
                      key={timeString}
                      className={`timepicker-simple-option ${
                        value === timeString ? "selected" : ""
                      }`}
                      onClick={() => handleSimpleTimeSelect(timeString)}
                    >
                      {value === timeString && (
                        <span className="icon-space">
                          <Icon fileName="check-sm-18" isHidden={true} style={{ marginRight: "5px" }} />
                        </span>
                      )}
                      {value !== timeString && <span className="icon-space"></span>}
                      {timeString}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="timepicker-columns">
                    {/* Hours Column */}
                    <div className="timepicker-column hour-column" ref={hoursRef}>
                      {hourOptions.map((hour) => (
                        <div
                          key={hour}
                          className={`timepicker-option hour-option ${
                            tempTime && tempTime.hour === hour ? "selected" : ""
                          }`}
                          onClick={() => handleHourSelect(hour)}
                        >
                          {format === "12" ? hour : hour.toString().padStart(2, "0")}
                        </div>
                      ))}
                    </div>

                    {/* Minutes Column */}
                    <div className="timepicker-column minute-column" ref={minutesRef}>
                      {minuteOptions.map((minute) => (
                        <div
                          key={minute}
                          className={`timepicker-option minute-option ${
                            tempTime && tempTime.minute === minute ? "selected" : ""
                          }`}
                          onClick={() => handleMinuteSelect(minute)}
                        >
                          {minute.toString().padStart(2, "0")}
                        </div>
                      ))}
                    </div>

                    {/* AM/PM Column (only for 12-hour format) */}
                    {format === "12" && (
                      <div className="timepicker-column ampm-column" ref={ampmRef}>
                        <div
                          className={`timepicker-option ampm-option ${
                            tempTime && tempTime.ampm === "AM" ? "selected" : ""
                          }`}
                          onClick={() => handleAmPmSelect("AM")}
                        >
                          AM
                        </div>
                        <div
                          className={`timepicker-option ampm-option ${
                            tempTime && tempTime.ampm === "PM" ? "selected" : ""
                          }`}
                          onClick={() => handleAmPmSelect("PM")}
                        >
                          PM
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="timepicker-actions">
                    <button
                      type="button"
                      className="timepicker-button timepicker-cancel"
                      onClick={handleCancel}
                    >
                      CANCEL
                    </button>
                    <button
                      type="button"
                      className="timepicker-button timepicker-ok"
                      onClick={handleOk}
                    >
                      OK
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {hasError && (
          <span className="pe-input--error_message">
            <Icon fileName="warning-sm-18" isHidden={true} />
            { errorMessage || formatError }
          </span>
        )}
      </div>
    </div>
  );
}
